import React, { useContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import './footer.css';
import useNavigation from '../../../shared/utils/useNavigation';
import { ROUTES } from '../../../shared/constants/routes';
import { MatrixContext } from '../../../MatrixProvider';
import {
  isUserLoggedIn,
  isUserContentCreator,
  isUserAdmin,
  getUserProfilePicture,
  getUserRole,
} from '../../../shared/utils/function';
import { fetchNotificationCount } from '../../../shared/actions/notificationAction';
import {
  NOTIFICATION_COUNT_POLL_INTERVAL_MS,
  getFetchableActivityTabIds,
} from '../../../shared/constants/activity';
import { getActivityFooterUnreadBadgeSource } from '../../../shared/utils/activityUtil';
import ModernSideMenu from '../SlideMenu';
import UploadContent from '../../../views/Modals/UploadContent'; // upload content modal
import { wasSlideMenuOpen } from '../../../shared/utils/navigationState';
import LazyImage from '../../LazyImage';

/** Exact route or sub-path match (avoids false positives from path.includes). */
const isFooterRouteMatch = (path, route) =>
  path === route || path.startsWith(`${route}/`);

/** Single active footer tab from pathname — mutually exclusive per branch. */
const resolveActiveFooterTab = (path, isCreator) => {
  if (isCreator) {
    if (isFooterRouteMatch(path, ROUTES.MY_MESSAGES)) return 'messages';
    if (isFooterRouteMatch(path, ROUTES.MY_PROFILE)) return 'my-profile';
    if (isFooterRouteMatch(path, ROUTES.ACTIVITY)) return 'activity';
    return '';
  }

  if (
    path === ROUTES.ROOT ||
    path === ROUTES.MODERN_SUBSCRIPTIONS ||
    path === ROUTES.MODERN_PURCHASED ||
    isFooterRouteMatch(path, ROUTES.MODERN_FEED)
  ) {
    return 'home';
  }
  if (isFooterRouteMatch(path, ROUTES.MY_MESSAGES)) return 'messages';
  if (isFooterRouteMatch(path, ROUTES.DISCOVER)) return 'discover';
  if (isFooterRouteMatch(path, ROUTES.ACTIVITY)) return 'activity';
  return '';
};

const ModernFootermenu = ({
  onUploadSuccess,
  user, // coming from mapStateToProps
  creator, // coming from mapStateToProps
  notificationCount, // coming from mapStateToProps
  activityByTab,
  fetchNotificationCount, // coming from mapDispatchToProps
}) => {
  const navigate = useNavigation();
  const location = useLocation();
  const isLoggedIn = isUserLoggedIn();
  const isContentCreator = isUserContentCreator();
  const isAdmin = isUserAdmin();
  const { unreadTotal } = useContext(MatrixContext);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(true);

  useEffect(() => {
    let lastScrollY = window.pageYOffset || document.documentElement.scrollTop;
    let ticking = false;

    const updateScrollDir = (scrollY) => {
      // DEBUG: Uncomment the following line if it still doesn't work to see scroll values
      
      // Always show at the very top
      if (scrollY < 10) {
        setIsFooterVisible(true);
        lastScrollY = scrollY;
        ticking = false;
        return;
      }

      // Threshold to prevent flickering
      if (Math.abs(scrollY - lastScrollY) < 10) {
        ticking = false;
        return;
      }

      // If scrolling down (scrollY increased), hide; if scrolling up, show
      setIsFooterVisible(scrollY < lastScrollY);
      lastScrollY = scrollY;
      ticking = false;
    };

    const handleScroll = (e) => {
      let scrollY = 0;
      if (e.target === document || e.target === window) {
        scrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
      } else if (e.target && typeof e.target.scrollTop === 'number') {
        scrollY = e.target.scrollTop;
      } else {
        return;
      }

      if (!ticking) {
        window.requestAnimationFrame(() => updateScrollDir(scrollY));
        ticking = true;
      }
    };

    // Use capture: true to catch scroll events from any nested element
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const messageCount = useMemo(() => {
    if (!isLoggedIn) return 0;
    const n = Number(unreadTotal);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [isLoggedIn, unreadTotal]);

  const userRoleFooter = useMemo(() => getUserRole(), []);

  const activityFooterBadgeCount = useMemo(() => {
    if (!isLoggedIn) return 0;
    const tabIds = getFetchableActivityTabIds(userRoleFooter).filter(
      (id) => id !== 'archived'
    );
    const { sum, anyFetched } = getActivityFooterUnreadBadgeSource(
      activityByTab,
      tabIds
    );
    // Loaded activity rows are the source of truth; API count only before first fetch.
    return anyFetched ? sum : notificationCount;
  }, [activityByTab, isLoggedIn, notificationCount, userRoleFooter]);

  /** Derived from the URL so the correct icon is active on first paint (no flash on Messaging). */
  const activeFooterTab = useMemo(
    () => resolveActiveFooterTab(location.pathname, isContentCreator),
    [location.pathname, isContentCreator]
  );

  // After programmatic navigation (login redirect, edit profile, etc.), clear touch/focus
  // on footer items so a previously tapped tab does not stay pink alongside .active.
  useEffect(() => {
    document.querySelectorAll('.footer-nav-item').forEach((el) => {
      if (el instanceof HTMLElement) {
        el.blur();
      }
    });
  }, [location.pathname]);

  // Fetch notification count on mount and every 4 minutes while logged in
  useEffect(() => {
    if (!isLoggedIn) {
      return undefined;
    }
    fetchNotificationCount();
    const id = setInterval(() => {
      fetchNotificationCount();
    }, NOTIFICATION_COUNT_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isLoggedIn, fetchNotificationCount]);


  const handleFooterNavigation = (tab, event) => {
    switch (tab) {
      case 'home':
        navigate.toFeed();
        break;
      case 'discover':
        navigate.toDiscover();
        break;
      case 'my-profile':
        navigate.toProfile();
        break;
      case 'messages':
        navigate.toMessages();
        break;
      case 'activity':
        navigate.toActivity();
        break;
      case 'menu':
        setSideMenuOpen(prev => !prev);
        event?.currentTarget?.blur?.();
        return;
      default:
        return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Clear touch/focus highlight so only the URL-derived .active tab stays pink.
    event?.currentTarget?.blur?.();
  };

  useEffect(() => {
    const path = location.pathname;
    // Always consume (clear) the "menu was open" flag to avoid stale auto-open later,
    // but never auto-open the menu on the WebView page (it would cover the iframe).
    const shouldReopenMenu = wasSlideMenuOpen();
    const isWebViewPage = path === '/webview' || path.startsWith('/webview/');
    if (shouldReopenMenu && !isWebViewPage) {
      setTimeout(() => setSideMenuOpen(true), 100);
    }
  }, [location.pathname]);

  return (
    <>
      <footer className={`modern-footer ${!isFooterVisible ? 'cm_hidden' : ''}`}>
        <div className="footer-nav">
          {isLoggedIn && !isContentCreator && !isAdmin && (
            <div
              className={`footer-nav-item ${activeFooterTab === 'home' ? 'active' : ''}`}
              onClick={(event) => handleFooterNavigation('home', event)}
            >
              <i className="fa-solid fa-house"></i>
              <span>Home</span>
            </div>
          )}
          {!isLoggedIn && (
            <div
              className="footer-nav-item login-btn"
              onClick={() => navigate.toLogin()}
            >
              <i className="fa-solid fa-sign-in-alt"></i>
              <span>Login</span>
            </div>
          )}
          {!isContentCreator && (
            <div
              className={`footer-nav-item ${activeFooterTab === 'discover' ? 'active' : ''}`}
              onClick={(event) => handleFooterNavigation('discover', event)}
            >
              <i className="fa-solid fa-compass"></i>
              <span>Discover</span>
            </div>
          )}
          {isLoggedIn && isContentCreator && (
            <div
              className={`footer-nav-item ${activeFooterTab === 'my-profile' ? 'active' : ''}`}
              onClick={(event) => handleFooterNavigation('my-profile', event)}
            >
              <i className="fa-solid fa-user"></i>
              <span>My Profile</span>
            </div>
          )}

          {isLoggedIn && !isAdmin && (
            <div
              className={`footer-nav-item messages-button ${activeFooterTab === 'messages' ? 'active' : ''}`}
              onClick={(event) => handleFooterNavigation('messages', event)}
            >
              <i className="fa-solid fa-comment"></i>
              <span>Messaging</span>
              {messageCount > 0 &&
                <div className="notification-badge notification-badge-messages">
                  {messageCount}
                </div>
              }
            </div>
          )}

          {/* Upload Button - Center (3rd position) */}
          {isLoggedIn && isContentCreator && (
            <div className="footer-upload-button">
              <UploadContent
                forFooter={true}
                onUploadSuccess={onUploadSuccess}
              />
            </div>
          )}

          {isLoggedIn && !isAdmin && (
            <div
              className={`footer-nav-item ${activeFooterTab === 'activity' ? 'active' : ''}`}
              onClick={(event) => handleFooterNavigation('activity', event)}
            >
              <i className="fa-solid fa-heart"></i>
              <span>Activity</span>
              {activityFooterBadgeCount > 0 &&
                <div className="notification-badge notification-badge-activity">{activityFooterBadgeCount}</div>
              }
            </div>
          )}

          <div
            className={`footer-nav-item ${activeFooterTab === 'menu' ? 'active' : ''}`}
            onClick={(event) => handleFooterNavigation('menu', event)}
          >
            <span className='cm_footer_avatar'>
              <LazyImage
                src={getUserProfilePicture(user, creator)}
                alt="User Profile"
                className="footer-avatar"
              />
            </span>
            <span>Menu</span>
          </div>
        </div>

      </footer>



      {/* Modern Side Menu */}
      <ModernSideMenu
        isOpen={sideMenuOpen}
        onClose={() => setSideMenuOpen(false)}
      />
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user?.user,
    creator: state.creator?.creator,
    notificationCount: state.notifications?.count || 0,
    activityByTab: state.activity?.byTab || {},
  };
};

const mapDispatchToProps = {
  fetchNotificationCount,
};

export default connect(mapStateToProps, mapDispatchToProps)(ModernFootermenu); 